import React, { useState } from 'react'
import { Button } from 'components/ui/button'
import { Select } from 'components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from 'components/ui/dialog'
import { FileDown } from 'lucide-react'
import { useFileExport } from '../hooks'
import type { ExportModalProps, ExportOptions } from '../types/financials.types'

export const ExportModal: React.FC<ExportModalProps> = ({
	isOpen,
	onClose,
	incomeStatement,
	onExport,
}) => {
	const { isExporting, exportError, exportFile } = useFileExport()

	const [format, setFormat] = useState<'xlsx' | 'csv'>('xlsx')
	const [selectedQuarters, setSelectedQuarters] = useState<string[]>(
		incomeStatement.quarters.map(q => q.id),
	)
	const [includeCalculatedFields, setIncludeCalculatedFields] = useState(true)
	const [includeFormulas, setIncludeFormulas] = useState(false)
	const [includeDescriptions, setIncludeDescriptions] = useState(false)
	const [template, setTemplate] = useState<'standard' | 'investor' | 'tax'>('standard')

	const handleExport = async () => {
		const options: ExportOptions = {
			format,
			quarters: selectedQuarters,
			includeCalculatedFields,
			includeFormulas: format === 'xlsx' && includeFormulas,
			includeDescriptions,
			template,
		}

		try {
			await exportFile(incomeStatement, options)
			await onExport(options)
			onClose()
		} catch (error) {
			console.error('Export error:', error)
		}
	}

	const toggleQuarter = (quarterId: string) => {
		setSelectedQuarters(prev =>
			prev.includes(quarterId) ? prev.filter(id => id !== quarterId) : [...prev, quarterId],
		)
	}

	const toggleAllQuarters = () => {
		if (selectedQuarters.length === incomeStatement.quarters.length) {
			setSelectedQuarters([])
		} else {
			setSelectedQuarters(incomeStatement.quarters.map(q => q.id))
		}
	}

	const getFileName = () => {
		const storeName = incomeStatement.location || 'Store'
		const date = new Date().toISOString().split('T')[0]
		const extension = format === 'xlsx' ? 'xlsx' : 'csv'
		return `${storeName}_Income_Statement_${date}.${extension}`
	}

	return (
		<Dialog open={isOpen} onClose={onClose}>
			<DialogContent className="max-w-2xl">
				<DialogHeader>
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
							<FileDown className="w-5 h-5" />
						</div>
						<div>
							<DialogTitle>Export Income Statement</DialogTitle>
							<DialogDescription>Download your financial data as a file</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<div className="px-5 py-4 space-y-6">
					{/* File Format */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-2">File Format:</label>
						<div className="flex space-x-4">
							<label className="flex items-center">
								<input
									type="radio"
									value="xlsx"
									checked={format === 'xlsx'}
									onChange={e => setFormat(e.target.value as 'xlsx')}
									className="h-4 w-4 text-blue-600 focus:ring-blue-500"
								/>
								<span className="ml-2 text-sm text-gray-700">Excel (.xlsx)</span>
							</label>
							<label className="flex items-center">
								<input
									type="radio"
									value="csv"
									checked={format === 'csv'}
									onChange={e => setFormat(e.target.value as 'csv')}
									className="h-4 w-4 text-blue-600 focus:ring-blue-500"
								/>
								<span className="ml-2 text-sm text-gray-700">CSV (.csv)</span>
							</label>
						</div>
					</div>

					<div className="border-t border-gray-200 pt-4" />

					{/* Select Quarters */}
					<div>
						<div className="flex items-center justify-between mb-2">
							<label className="block text-sm font-medium text-gray-700">
								Select Quarters to Export:
							</label>
							<button
								onClick={toggleAllQuarters}
								className="text-xs text-blue-600 hover:text-blue-700 font-medium"
							>
								{selectedQuarters.length === incomeStatement.quarters.length
									? 'Deselect All'
									: 'Select All'}
							</button>
						</div>
						<div className="grid grid-cols-3 gap-2">
							{[...incomeStatement.quarters]
								.sort((a, b) => b.order - a.order) // Sort descending (most recent first)
								.map(quarter => (
									<label
										key={quarter.id}
										className="flex items-center p-2 border border-gray-200 rounded hover:bg-gray-50 cursor-pointer"
									>
										<input
											type="checkbox"
											checked={selectedQuarters.includes(quarter.id)}
											onChange={() => toggleQuarter(quarter.id)}
											className="h-4 w-4 text-blue-600 focus:ring-blue-500 rounded"
										/>
										<span className="ml-2 text-sm text-gray-700">{quarter.label}</span>
									</label>
								))}
						</div>
					</div>

					<div className="border-t border-gray-200 pt-4" />

					{/* Export Options */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-2">Export Options:</label>
						<div className="space-y-2">
							<label className="flex items-center">
								<input
									type="checkbox"
									checked={includeCalculatedFields}
									onChange={e => setIncludeCalculatedFields(e.target.checked)}
									className="h-4 w-4 text-blue-600 focus:ring-blue-500 rounded"
								/>
								<span className="ml-2 text-sm text-gray-700">Include calculated fields</span>
							</label>

							{format === 'xlsx' && (
								<label className="flex items-center">
									<input
										type="checkbox"
										checked={includeFormulas}
										onChange={e => setIncludeFormulas(e.target.checked)}
										className="h-4 w-4 text-blue-600 focus:ring-blue-500 rounded"
									/>
									<span className="ml-2 text-sm text-gray-700">
										Include formulas <span className="text-gray-500">(Excel only)</span>
									</span>
								</label>
							)}

							<label className="flex items-center">
								<input
									type="checkbox"
									checked={includeDescriptions}
									onChange={e => setIncludeDescriptions(e.target.checked)}
									className="h-4 w-4 text-blue-600 focus:ring-blue-500 rounded"
								/>
								<span className="ml-2 text-sm text-gray-700">Include line item descriptions</span>
							</label>
						</div>
					</div>

					<div className="border-t border-gray-200 pt-4" />

					{/* Export Template */}
					<div>
						<label htmlFor="export-template" className="block text-sm font-medium text-gray-700 mb-2">Export Template:</label>
						<Select
							id="export-template"
							value={template}
							onChange={e => setTemplate(e.target.value as 'standard' | 'investor' | 'tax')}
						>
							<option value="standard">Standard</option>
							<option value="investor">Investor Format</option>
							<option value="tax">Tax Format</option>
						</Select>
						<p className="mt-1 text-xs text-gray-500">
							Different templates format the data for specific use cases
						</p>
					</div>

					<div className="border-t border-gray-200 pt-4" />

					{/* File Preview */}
					<div className="bg-gray-50 rounded-lg p-4">
						<h4 className="text-sm font-medium text-gray-900 mb-1">File will be saved as:</h4>
						<p className="text-sm text-gray-600 font-mono">{getFileName()}</p>
					</div>

					{exportError && (
						<div className="bg-red-50 border border-red-200 rounded-lg p-3">
							<p className="text-sm text-red-800">⚠ Error: {exportError.message}</p>
						</div>
					)}
				</div>

				<DialogFooter>
					<Button variant="ghost" onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant="success"
						onClick={handleExport}
						disabled={selectedQuarters.length === 0 || isExporting}
					>
						<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
							/>
						</svg>
						{isExporting ? 'Exporting...' : 'Export'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
